// Determine API URL based on environment
const getApiUrl = () => {
  const environment = process.env.REACT_APP_ENVIRONMENT;
  
  if (environment === 'staging') {
    return process.env.REACT_APP_API_URL_STAGING;
  } else if (environment === 'production') {
    return process.env.REACT_APP_API_URL_PRODUCTION;
  }
  
  // Default to staging if environment not specified
  return process.env.REACT_APP_API_URL_STAGING;
};

const apiUrl = getApiUrl();

if (!apiUrl) {
  throw new Error('API URL is not set — check that the correct .env file was loaded for this build');
}

// Public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = [
  '/api/auth/send-otp',
  '/api/auth/verify-otp',
  '/api/auth/verify-mobileNumber'
];

// Endpoints where 401 should be treated as a regular error (not auto-redirect)
const AUTH_ERROR_ENDPOINTS = [
  '/api/auth/verify-mobileNumber'
];

export const apiClient = {
  baseURL: apiUrl,
  
  async request(endpoint, config = {}) {
    const isPublicEndpoint = PUBLIC_ENDPOINTS.some(publicEndpoint => endpoint.startsWith(publicEndpoint));
    const isAuthErrorEndpoint = AUTH_ERROR_ENDPOINTS.some(authEndpoint => endpoint.startsWith(authEndpoint));
    const token = !isPublicEndpoint ? localStorage.getItem('access_token') : null;
    const url = `${this.baseURL}${endpoint}`;
    
    const requestConfig = {
      method: config.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...config.headers,
      },
      ...(config.body && { body: JSON.stringify(config.body) }),
    };

    console.log('🌐 ApiClient Request:', {
      method: requestConfig.method,
      url,
      endpoint,
      baseURL: this.baseURL,
      headers: requestConfig.headers,
      body: config.body,
      timestamp: new Date().toISOString()
    });

    try {
      const response = await fetch(url, requestConfig);
      
      console.log('📡 ApiClient Response:', {
        url,
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        timestamp: new Date().toISOString()
      });
      
      // Only auto-redirect on 401 for non-auth endpoints
      if (response.status === 401 && !isAuthErrorEndpoint) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.reload();
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ ApiClient Error Response:', {
          url,
          status: response.status,
          errorData,
          timestamp: new Date().toISOString()
        });
        
        // Create error with response data attached
        const errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}`;
        const error = new Error(errorMessage);
        error.response = {
          status: response.status,
          data: errorData
        };
        error.status = response.status;
        throw error;
      }
      
      const data = await response.json();
      console.log('✅ ApiClient Success Response:', {
        url,
        dataType: typeof data,
        dataLength: Array.isArray(data) ? data.length : Object.keys(data || {}).length,
        data: data,
        timestamp: new Date().toISOString()
      });
      return { data, success: true };
    } catch (error) {
      console.error('💥 ApiClient Request Failed:', {
        url,
        error: error.message,
        status: error.status,
        response: error.response,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  },

  get(endpoint, config) {
    return this.request(endpoint, { ...config, method: 'GET' });
  },

  post(endpoint, body, config) {
    return this.request(endpoint, { ...config, method: 'POST', body });
  },

  put(endpoint, body, config) {
    return this.request(endpoint, { ...config, method: 'PUT', body });
  },

  delete(endpoint, config) {
    return this.request(endpoint, { ...config, method: 'DELETE' });
  }
};
